var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pane, PaneFooter, Button, TextField, Select, Row, Col, Loading, Callout, ConfirmationModal, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { createOrganizationSchema, } from "../utils/validationUtils";
import { useModal } from "../utils/useModal";
import { getErrorInfo, isOptimisticLockConflict, } from "../utils/getErrorCode";
import { getOrganizationErrorMessageId } from "../utils/getErrorMessageId";
import useNavigation from "../hooks/useNavigation";
import ActionErrorModal from "../components/ActionErrorModal";
var OrganizationForm = function (_a) {
    var match = _a.match;
    var id = useParams().id;
    var goToList = useNavigation(match.path).goToList;
    var intl = useIntl();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useModal(), isOpen = _b.isOpen, message = _b.message, openModal = _b.openModal, closeModal = _b.closeModal;
    var calloutRef = useRef(null);
    var _c = useState(false), showUnsavedModal = _c[0], setShowUnsavedModal = _c[1];
    var _d = useState(false), showConflictModal = _d[0], setShowConflictModal = _d[1];
    var isEditing = Boolean(id);
    var schema = useMemo(function () { return createOrganizationSchema(intl); }, [intl]);
    var _e = useForm({
        resolver: zodResolver(schema),
        defaultValues: { status: "ACTIVE" },
        mode: "onBlur",
    }), handleSubmit = _e.handleSubmit, control = _e.control, reset = _e.reset, _f = _e.formState, errors = _f.errors, isSubmitting = _f.isSubmitting, isDirty = _f.isDirty;
    var _g = useQuery(["czna-organizations", "organization", id], function () { return ky.get("czna-users/organizations/".concat(id)).json(); }, { enabled: isEditing }), organization = _g.data, isLoading = _g.isLoading, loadError = _g.error, refetch = _g.refetch;
    /**
     * The form takes the loaded organization once, on its initial load. After that only an
     * explicit reload resets it ({@link handleConflictReload}): a refetch must not overwrite
     * what the admin is typing. Resetting on every change of `organization` also made the
     * conflict reload reset the form TWICE — once in the handler, once here when the refetched
     * data reached the render — in no fixed order, so an edit typed between the two was
     * silently replaced by the reloaded value (and Uložit disabled again). UserForm loads its
     * baseline the same way (captureServerUser).
     */
    var loadedIdRef = useRef(undefined);
    useEffect(function () {
        // By id, so a route that switches to another organization still loads it.
        if (organization && loadedIdRef.current !== organization.id) {
            loadedIdRef.current = organization.id;
            reset(organization);
        }
    }, [organization, reset]);
    useEffect(function () {
        if (isEditing && loadError) {
            goToList();
        }
    }, [isEditing, loadError, goToList]);
    var saveMutation = useMutation(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!isEditing) return [3 /*break*/, 2];
                    return [4 /*yield*/, ky.put("czna-users/organizations/".concat(id), { json: values })];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, ky.post("czna-users/organizations", { json: values })];
                case 3:
                    _a.sent();
                    _a.label = 4;
                case 4: return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function () {
            var _a;
            queryClient.invalidateQueries(["czna-organizations"]);
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: "success",
                message: intl.formatMessage({
                    id: isEditing
                        ? "ui-czna-users.organizations.update.success"
                        : "ui-czna-users.organizations.create.success",
                }),
            });
            goToList();
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(error)];
                    case 1:
                        errorInfo = _a.sent();
                        // CZNA-1603: by the code, not the status — the last administrator is a 409 too.
                        if (isOptimisticLockConflict(errorInfo)) {
                            setShowConflictModal(true);
                        }
                        else {
                            openModal(intl.formatMessage({
                                id: getOrganizationErrorMessageId(errorInfo),
                            }));
                        }
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var handleClose = function () {
        if (isDirty) {
            setShowUnsavedModal(true);
        }
        else {
            goToList();
        }
    };
    var onSubmit = function (values) {
        saveMutation.mutate(values);
    };
    /**
     * CZNA-1603: loads the organization as the server holds it now and resets the form to it,
     * version included, so the next save is built on the current state. The ONLY reset after
     * the initial load (see the effect above).
     */
    var handleConflictReload = function () { return __awaiter(void 0, void 0, void 0, function () {
        var result, errorInfo;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setShowConflictModal(false);
                    return [4 /*yield*/, refetch()];
                case 1:
                    result = _a.sent();
                    if (!(result.isSuccess && result.data)) return [3 /*break*/, 2];
                    reset(result.data);
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, getErrorInfo(result.error)];
                case 3:
                    errorInfo = _a.sent();
                    openModal(intl.formatMessage({ id: getOrganizationErrorMessageId(errorInfo) }));
                    _a.label = 4;
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var statusOptions = useMemo(function () { return [
        {
            value: "ACTIVE",
            label: intl.formatMessage({ id: "ui-czna-users.status.active" }),
        },
        {
            value: "INACTIVE",
            label: intl.formatMessage({ id: "ui-czna-users.status.inactive" }),
        },
    ]; }, [intl]);
    if (isEditing && (isLoading || loadError)) {
        return React.createElement(Loading, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { noValidate: true, onSubmit: handleSubmit(onSubmit) },
            React.createElement(Pane, { defaultWidth: "fill", paneTitle: isEditing ? (React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.edit" })) : (React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.new" })), onClose: handleClose, dismissible: true, footer: React.createElement(PaneFooter, { renderEnd: React.createElement(React.Fragment, null,
                        React.createElement(Button, { marginBottom0: true, onClick: handleClose },
                            React.createElement(FormattedMessage, { id: "ui-czna-users.cancel" })),
                        React.createElement(Button, { type: "submit", buttonStyle: "primary", marginBottom0: true, disabled: !isDirty || isSubmitting },
                            React.createElement(FormattedMessage, { id: "ui-czna-users.save" }))) }) },
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(Controller, { control: control, name: "name", render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(TextField, __assign({}, field, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.name" }), error: (_b = errors.name) === null || _b === void 0 ? void 0 : _b.message, required: true })));
                            } })),
                    React.createElement(Col, { xs: 6 },
                        React.createElement(Controller, { control: control, name: "status", render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(Select, __assign({}, field, { dataOptions: statusOptions, label: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.status" }), error: (_b = errors.status) === null || _b === void 0 ? void 0 : _b.message })));
                            } }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(Controller, { control: control, name: "sigla", render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(TextField, __assign({}, field, { value: field.value || "", onChange: function (e) {
                                        return field.onChange(e.target.value.toUpperCase());
                                    }, label: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.sigla" }), placeholder: intl.formatMessage({
                                        id: "ui-czna-users.organizations.sigla.placeholder",
                                    }), maxLength: 6, error: (_b = errors.sigla) === null || _b === void 0 ? void 0 : _b.message })));
                            } }))))),
        React.createElement(ActionErrorModal, { open: isOpen, message: message, onClose: closeModal }),
        React.createElement(ConfirmationModal, { open: showUnsavedModal, heading: intl.formatMessage({
                id: "ui-czna-users.unsavedChanges.heading",
            }), message: intl.formatMessage({
                id: "ui-czna-users.unsavedChanges.message",
            }), confirmLabel: intl.formatMessage({
                id: "ui-czna-users.unsavedChanges.confirm",
            }), cancelLabel: intl.formatMessage({
                id: "ui-czna-users.unsavedChanges.cancel",
            }), onConfirm: goToList, onCancel: function () { return setShowUnsavedModal(false); } }),
        React.createElement(ConfirmationModal, { open: showConflictModal, heading: intl.formatMessage({
                id: "ui-czna-users.error.optimisticLockConflict.heading",
            }), message: intl.formatMessage({
                id: "ui-czna-users.error.optimisticLockConflict.message",
            }), confirmLabel: intl.formatMessage({
                id: "ui-czna-users.error.optimisticLockConflict.reload",
            }), cancelLabel: intl.formatMessage({
                id: "ui-czna-users.cancel",
            }), onConfirm: handleConflictReload, onCancel: function () { return setShowConflictModal(false); } }),
        React.createElement(Callout, { ref: calloutRef })));
};
export default OrganizationForm;
